import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Authenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { useUser } from "../contexts/UserContext";
import { graphqlUserPool } from "../utilities/graphqlUserPool";
import { confirmEolVerificationDocument } from "../graphql/customQueries";
import { logToConsole } from "../utilities/utilities";
import "../styleSheets/profile.css";

type VerificationOutcome =
  | "SUCCESS"
  | "LINK_EXPIRED"
  | "ALREADY_VERIFIED"
  | "INVALID_LINK";

const inFlightConfirmations = new Map<string, Promise<VerificationOutcome>>();

async function confirmVerificationToken(
  token: string,
): Promise<VerificationOutcome> {
  const normalizedToken = String(token || "").trim();
  if (!normalizedToken) {
    return "INVALID_LINK";
  }

  const pendingOutcome = inFlightConfirmations.get(normalizedToken);
  if (pendingOutcome) {
    return pendingOutcome;
  }

  const request = (async () => {
    try {
      const data: any = await graphqlUserPool<any>({
        query: confirmEolVerificationDocument,
        variables: { token: normalizedToken },
      });

      const outcome = String(
        data?.confirmEolVerification?.outcome || "INVALID_LINK",
      ).trim() as VerificationOutcome;
      return outcome || "INVALID_LINK";
    } catch (error) {
      logToConsole("error", "Failed to confirm EOL verification:", error);
      return "INVALID_LINK";
    } finally {
      inFlightConfirmations.delete(normalizedToken);
    }
  })();

  inFlightConfirmations.set(normalizedToken, request);
  return request;
}

function Verified() {
  const location = useLocation();
  const { t } = useTranslation("common");
  const { isAuthenticated, isAuthLoading, isUserDataLoading } = useUser();
  const [isLoading, setIsLoading] = useState(true);
  const [outcome, setOutcome] = useState<VerificationOutcome>("INVALID_LINK");
  const [hasConfirmed, setHasConfirmed] = useState(false);

  const token = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return String(params.get("token") || "").trim();
  }, [location.search]);

  useEffect(() => {
    if (!token) {
      setOutcome("INVALID_LINK");
      setIsLoading(false);
      return;
    }

    // Wait for auth state to resolve before deciding whether to prompt for
    // sign-in or proceed — avoids a flash of the sign-in form for a user who
    // is already logged in on this device.
    if (isAuthLoading || (isAuthenticated && isUserDataLoading)) {
      setIsLoading(true);
      return;
    }

    if (!isAuthenticated) {
      // Verification emails are sent only to the message author, who has an
      // Eterniq account — this page keeps the token in place and prompts
      // sign-in inline rather than navigating away, so the token is never lost.
      setIsLoading(false);
      return;
    }

    if (hasConfirmed) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    void (async () => {
      const result = await confirmVerificationToken(token);
      if (!cancelled) {
        setOutcome(result);
        setHasConfirmed(true);
        setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, isAuthenticated, isAuthLoading, isUserDataLoading, hasConfirmed]);

  const messageKey =
    outcome === "SUCCESS"
      ? "verified.success"
      : outcome === "LINK_EXPIRED"
        ? "verified.linkExpired"
        : outcome === "ALREADY_VERIFIED"
          ? "verified.alreadyVerified"
          : "verified.invalidLink";

  const showSignInPrompt = !!token && !isAuthLoading && !isAuthenticated;

  return (
    <div className="profile-page">
      <div className="profile-content">
        <section className="profile-introCard">
          <div className="profile-introCard__title">{t("verified.title")}</div>
          {showSignInPrompt ? (
            <>
              <p className="profile-introCard__text">
                {t("verified.signInRequired", {
                  defaultValue:
                    "Please sign in with your Eterniq account to confirm this verification link.",
                })}
              </p>
              <Authenticator
                signUpAttributes={["email", "given_name", "family_name", "nickname"]}
              />
            </>
          ) : (
            <p className="profile-introCard__text">
              {isLoading ? t("common.loading") : t(messageKey)}
            </p>
          )}
        </section>
      </div>
    </div>
  );
}

export default Verified;
